import '~/components/echarts/chart/chart.scss'

import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import * as echarts from 'echarts'
import { useEffect, useMemo, useRef } from 'react'
import { findChartInfo } from '~/components/echarts/chart/chart_info'
import { useEChartsColorScheme } from '~/components/echarts/color_scheme_context'
import { optionsForChart } from '~/components/echarts/options'
import Spinner from '~/components/spinner'

const noop = () => {}

const CHART_INFO_KEY_LABELS = () => ({
  meaningful: t`Meaningful`,
  units: t`Units`,
})

function modifyDataToHideLabels(chartData) {
  if (!chartData?.series) {
    return chartData
  }

  return {
    ...chartData,
    series: chartData.series.map((series) => ({
      ...series,
      label: { ...series.label, show: false },
    })),
  }
}

export function Chart({
  title,
  chartInfoTitle,
  data,
  isLoading,
  style,
  children,
  className,
  onChartClick = noop,
  showInfo = false,
  hideLabels = false,
}) {
  const echartRef = useRef()
  const chartDivRef = useRef()
  const chartInfo = useMemo(() => findChartInfo(chartInfoTitle), [chartInfoTitle])
  const echartsTheme = useEChartsColorScheme() === 'dark' ? 'dark' : null

  useEffect(() => {
    const echart = echarts.init(chartDivRef.current, echartsTheme, { height: 'auto', width: 'auto' })
    echartRef.current = echart

    const resizeObserver = new ResizeObserver(() => echart.resize())
    resizeObserver.observe(chartDivRef.current)
    resizeObserver.observe(document.querySelector('body'))

    return () => {
      echart.dispose()
      resizeObserver.disconnect()
    }
  }, [echartsTheme])

  useEffect(() => {
    const echart = echartRef.current
    const handler = (params) => onChartClick(params.name, params.componentType)
    echart.on('click', handler)
    return () => echart.off('click', handler)
  }, [onChartClick])

  useEffect(() => {
    if (!data?.type && !data?.chartType) return
    const echart = echartRef.current
    echart.clear()
    const chartData = hideLabels ? modifyDataToHideLabels(data) : data
    echart.setOption(optionsForChart(chartData, echartsTheme))
  }, [data, hideLabels, echartsTheme])

  // biome-ignore lint/correctness/useExhaustiveDependencies: showInfo triggers the resize when the info panel toggles
  useEffect(() => {
    echartRef.current.resize()
  }, [showInfo])

  return (
    <div className={['position-relative', className].filter(Boolean).join(' ')}>
      {isLoading && <Spinner />}

      <div className='chart-title'>
        <h4 className='text-center flex-grow-1 pt-3'>{title}</h4>
        {!isLoading && children}
      </div>

      <div className='chart-row'>
        <div
          ref={chartDivRef}
          className={['chart-wrapper', showInfo && 'collapsed'].filter(Boolean).join(' ')}
          style={style}
          data-testid='chart-wrapper'
        />

        {showInfo && (
          <div className='chart-info'>
            <div className='card h-100'>
              <div className='card-header'>
                <h3 className='card-title'>
                  <Trans>Chart Info</Trans>
                </h3>
              </div>

              <div className='card-body'>
                {chartInfo &&
                  Object.keys(chartInfo)
                    .filter((key) => key !== 'title' && key !== 'chart_info_title')
                    .map((key, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
                      <div key={index} className='mb-4'>
                        <h4 className='text-capitalize'>{CHART_INFO_KEY_LABELS()[key] ?? key}</h4>
                        <div className='text-justify'>{chartInfo[key]}</div>
                      </div>
                    ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
